import React from 'react';
import { FinancialValue } from '../shared/FinancialValue';
import { StateChip } from '../shared/StateChip';
import { HonestState } from '../workspace/primitives/HonestState';

export interface MethodologyLever {
  label: string;
  authority?: string;
  posture?: string;
  value?: number | null;
  notes?: string;
}

export interface MethodologySummary {
  headline?: {
    label?: string;
    authority?: string;
    posture?: string;
    value?: number | null;
    notes?: string;
  } | null;
  conditional_levers?: MethodologyLever[] | null;
}

interface MethodologyNarrativeCardProps {
  methodologySummary?: MethodologySummary | null;
}

// posture → the law's semantic state, the ONLY posture→hue logic. NOTE: the backend serializer
// currently drops the `contested` boolean (Fable #6), so the blocker branch is presently
// unreachable; it is kept for when the serializer passes `contested` through. Hue follows the
// posture, never the word "CONTESTED" appearing in the authored notes.
function postureToState(posture?: string): 'blocker' | 'needs-human' | 'verified' | 'dormant' {
  if (posture === 'conservative') return 'verified';
  if (posture === 'conditional') return 'needs-human';
  if (posture === 'contested' || posture === 'blocker') return 'blocker';
  return 'dormant';
}

export const MethodologyNarrativeCard: React.FC<MethodologyNarrativeCardProps> = ({ methodologySummary }) => {
  // Fable #2: `{headline: null, conditional_levers: []}` is truthy — guard on emptiness, not falsiness.
  if (
    !methodologySummary ||
    (methodologySummary.headline == null && (methodologySummary.conditional_levers?.length ?? 0) === 0)
  ) {
    return <HonestState tier="no-input" scale="panel" label="Methodology summary not available" />;
  }

  const { headline, conditional_levers } = methodologySummary;
  const hasLevers = (conditional_levers?.length ?? 0) > 0;

  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="px-5 h-12 flex items-center border-b border-[var(--color-border)]">
        <span className="text-[11px] uppercase tracking-[0.08em] font-medium text-[var(--color-text-muted)]">
          Methodology
        </span>
      </div>

      {headline != null && (
        <div className="px-5 py-4 border-b border-[var(--color-border)]">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-[var(--color-text-primary)] mb-1">{headline.label}</div>
              {headline.authority && (
                <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
                  {headline.authority}
                </div>
              )}
            </div>
            {/* Chip sits immediately left of the figure it governs (K3 §3). */}
            <StateChip state="verified" text="Conservative" />
            <div className="text-right shrink-0">
              <FinancialValue value={headline.value} size="md" />
            </div>
          </div>
          {headline.notes && (
            <div className="text-[13px] text-[var(--color-text-secondary)] mt-3">{headline.notes}</div>
          )}
        </div>
      )}

      {hasLevers && (
        <div className="px-5 py-4">
          <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-dim)] mb-4">
            CONDITIONAL LEVERS — NOT INCLUDED IN DEMAND
          </div>
          <div className="space-y-4">
            {conditional_levers!.map((lever, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-[var(--color-text-secondary)] mb-1">{lever.label}</div>
                  {lever.authority && (
                    <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
                      {lever.authority}
                    </div>
                  )}
                  {lever.notes && (
                    <div className="text-[13px] text-[var(--color-text-secondary)] mt-1">{lever.notes}</div>
                  )}
                </div>
                {/* Chip sits immediately left of the figure it conditions (K3 §3). */}
                <StateChip state={postureToState(lever.posture)} text="Conditional — verify election" />
                <div className="text-right shrink-0">
                  <div className="flex items-baseline justify-end text-[13px] text-[var(--color-text-secondary)]">
                    <span>+</span>
                    <FinancialValue value={lever.value} size="md" />
                  </div>
                  <div className="text-[11px] text-[var(--color-text-muted)] font-mono mt-1">excluded from total</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MethodologyNarrativeCard;
